import React, { useEffect, useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { Plus, FolderOpen, Users, X, FileSpreadsheet, Upload, Activity, AlertTriangle, Flag, Image as ImageIcon, FileText, Mail, DollarSign, Lock, Receipt, Wallet, Clock, ShieldCheck, AlertCircle } from "lucide-react";
import PortalLayout from "../../components/portal/PortalLayout";
import KpiFilterDrawer from "../../components/portal/KpiFilterDrawer";
import { portalApi } from "../../lib/portalApi";
import { usePortalAuth } from "../../contexts/PortalAuthContext";
import { usePortalLang } from "../../lib/PortalLanguageContext";
import GanttUploadWizard from "../../components/portal/TimelineTab";

const STATUS_LABEL = {
  planning: "Planning",
  active: "Active",
  delayed: "Delayed",
  awaiting_client: "Awaiting Client",
  completed: "Completed",
  handover: "Handover",
};
const STATUS_COLOR = {
  planning: "bg-[#7A6B5F] text-[#FAF5EC]",
  active: "bg-[#6E1F2C] text-[#FAF5EC]",
  delayed: "bg-[#A3141A] text-[#FAF5EC]",
  awaiting_client: "bg-[#D4A963] text-[#1C110C]",
  completed: "bg-[#1C110C] text-[#FAF5EC]",
  handover: "bg-[#56151F] text-[#FAF5EC]",
};

const ROLE_OPTIONS = [];

function SummaryCard({ icon: Icon, label, count, hint, accent = "maroon", testid, onClick }) {
  const accents = {
    maroon: {
      ring: "from-[#6E1F2C] to-[#56151F]",
      icon: "bg-[#6E1F2C] text-[#FAF5EC]",
      num: "text-[#1C110C]",
      stripe: "bg-[#6E1F2C]",
    },
    gold: {
      ring: "from-[#D4A963] to-[#A6822F]",
      icon: "bg-[#D4A963] text-[#1C110C]",
      num: "text-[#1C110C]",
      stripe: "bg-[#D4A963]",
    },
    red: {
      ring: "from-[#A3141A] to-[#6E0F14]",
      icon: "bg-[#A3141A] text-[#FAF5EC]",
      num: "text-[#A3141A]",
      stripe: "bg-[#A3141A]",
    },
    charcoal: {
      ring: "from-[#3E2A22] to-[#1C110C]",
      icon: "bg-[#1C110C] text-[#FAF5EC]",
      num: "text-[#1C110C]",
      stripe: "bg-[#1C110C]",
    },
  };
  const a = accents[accent] || accents.maroon;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="text-left relative bg-white border border-[#6E1F2C]/10 hover:border-[#6E1F2C]/40 hover:shadow-[0_8px_28px_-12px_rgba(110,31,44,0.25)] transition-all duration-300 overflow-hidden group cursor-pointer"
    >
      {/* Top gold accent line */}
      <div className={`absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r ${a.ring}`} />
      <div className="p-5 md:p-6">
        <div className="flex items-start justify-between mb-4">
          <div className={`w-10 h-10 flex items-center justify-center ${a.icon} group-hover:scale-105 transition-transform`}>
            <Icon size={18} strokeWidth={1.8} />
          </div>
        </div>
        <p className={`font-serif-display text-4xl ${a.num} leading-none tracking-tight`}>
          {count}
        </p>
        <p className="text-[10px] tracking-[0.28em] uppercase text-[#3E2A22] mt-3 font-medium">
          {label}
        </p>
        {hint && (
          <p className="text-[10px] text-[#7A6B5F] mt-1.5 truncate italic">
            {hint}
          </p>
        )}
      </div>
    </button>
  );
}

function SummaryStrip() {
  const { t } = usePortalLang();
  const [s, setS] = useState(null);
  const [openKind, setOpenKind] = useState(null);
  useEffect(() => {
    portalApi.adminSummary().then(setS).catch(() => setS(null));
  }, []);
  if (!s) return null;
  // Executive-style 6 KPI strip
  const delayedAtRisk = (s.delayed || 0) + (s.high_risk || 0);
  const cards = [
    {
      k: "active",
      icon: Activity,
      label: t("dash.kpi.active"),
      count: s.active,
      accent: "maroon",
      hint: t("dash.kpi.active.hint", null, { n: s.planning || 0 }),
    },
    {
      k: "delayed",
      icon: AlertTriangle,
      label: t("dash.kpi.delayed"),
      count: delayedAtRisk,
      accent: "red",
      hint: delayedAtRisk ? t("dash.kpi.delayed.hint") : t("dash.kpi.delayed.onTrack"),
    },
    {
      k: "reports-pending",
      icon: FileText,
      label: t("dash.kpi.reportsPending"),
      count: s.reports_pending,
      accent: "gold",
      hint: t("dash.kpi.reportsPending.hint"),
    },
    {
      k: "responses",
      icon: Mail,
      label: t("dash.kpi.responses"),
      count: s.pending_responses,
      accent: "maroon",
      hint: t("dash.kpi.responses.hint"),
    },
    {
      k: "milestones",
      icon: Flag,
      label: t("dash.kpi.milestones"),
      count: s.upcoming_milestones,
      accent: "gold",
      hint: t("dash.kpi.milestones.hint"),
    },
    {
      k: "uploads",
      icon: ImageIcon,
      label: t("dash.kpi.uploads"),
      count: s.recent_uploads,
      accent: "charcoal",
      hint: t("dash.kpi.uploads.hint"),
    },
  ];
  return (
    <div data-testid="portal-admin-summary" className="mb-10">
      <div className="flex items-center gap-3 mb-4">
        <span className="h-px flex-1 bg-gradient-to-r from-[#6E1F2C]/40 to-transparent" />
        <p className="text-[10px] tracking-[0.4em] uppercase text-[#6E1F2C] font-medium">
          {t("dash.section.liveOverview")}
        </p>
        <span className="h-px flex-1 bg-gradient-to-l from-[#6E1F2C]/40 to-transparent" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
        {cards.map((c) => (
          <SummaryCard
            key={c.k}
            testid={`portal-summary-${c.k}`}
            icon={c.icon}
            label={c.label}
            count={c.count}
            hint={c.hint}
            accent={c.accent}
            onClick={() => setOpenKind(c.k)}
          />
        ))}
      </div>
      {openKind && <KpiFilterDrawer key={openKind} kind={openKind} onClose={() => setOpenKind(null)} />}
    </div>
  );
}

function FinancialKpiStrip() {
  const { t } = usePortalLang();
  const [s, setS] = useState(null);
  useEffect(() => {
    portalApi.adminFinancialsSummary()
      .then(setS)
      .catch(() => setS({ total_value: 0, amount_invoiced: 0, amount_received: 0, balance_pending: 0, payment_milestones_due: 0, payment_milestones_overdue: 0 }));
  }, []);
  if (!s) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-7">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="bg-white border border-[#6E1F2C]/10 h-24 animate-pulse" />
        ))}
      </div>
    );
  }
  const fmt = (v) => {
    if (v === null || v === undefined) return "—";
    const n = Number(v);
    if (!isFinite(n)) return "—";
    if (n === 0) return "AED 0";
    if (Math.abs(n) >= 1_000_000) return `AED ${(n / 1_000_000).toFixed(2)}M`;
    if (Math.abs(n) >= 1_000) return `AED ${(n / 1_000).toFixed(0)}K`;
    return `AED ${n.toLocaleString("en-AE", { maximumFractionDigits: 0 })}`;
  };
  const due = (s.payment_milestones_due || 0) + (s.payment_milestones_overdue || 0);
  const cards = [
    { k: "value", icon: DollarSign, label: t("fin.kpi.totalValue"), value: fmt(s.total_value), hint: t("proj.count.other", null, { n: s.project_count || 0 }), accent: "maroon" },
    { k: "invoiced", icon: Receipt, label: t("fin.kpi.invoiced"), value: fmt(s.amount_invoiced), hint: s.total_value ? t("fin.kpi.portfolioPct", null, { pct: ((s.amount_invoiced / s.total_value) * 100).toFixed(0) }) : "", accent: "gold" },
    { k: "received", icon: Wallet, label: t("fin.kpi.received"), value: fmt(s.amount_received), hint: t("fin.kpi.balance", null, { amount: fmt(s.balance_pending) }), accent: "green" },
    { k: "due", icon: AlertTriangle, label: t("fin.kpi.paymentMilestones"), value: `${due}`, hint: t("fin.kpi.dueOverdue", null, { due: s.payment_milestones_due || 0, overdue: s.payment_milestones_overdue || 0 }), accent: (s.payment_milestones_overdue || 0) > 0 ? "red" : "maroon" },
  ];
  const accents = {
    maroon: { bar: "bg-[#6E1F2C]", icon: "bg-[#6E1F2C] text-white", num: "text-[#1C110C]" },
    gold: { bar: "bg-[#D4A963]", icon: "bg-[#D4A963] text-[#1C110C]", num: "text-[#1C110C]" },
    green: { bar: "bg-[#3F6E2C]", icon: "bg-[#3F6E2C] text-white", num: "text-[#1C110C]" },
    red: { bar: "bg-[#A3141A]", icon: "bg-[#A3141A] text-white", num: "text-[#A3141A]" },
  };
  return (
    <div className="mb-7" data-testid="portal-financial-kpi-strip">
      <div className="flex items-center gap-2 mb-3">
        <Lock size={10} className="text-[#A6822F]" />
        <p className="text-[10px] tracking-[0.32em] uppercase text-[#A6822F]">{t("dash.section.adminFinancials")}</p>
        <div className="flex-1 h-px bg-gradient-to-r from-[#D4A963]/40 to-transparent" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map((c) => {
          const Icon = c.icon;
          const a = accents[c.accent] || accents.maroon;
          return (
            <div
              key={c.k}
              data-testid={`portal-financial-kpi-${c.k}`}
              className="relative bg-white border border-[#6E1F2C]/12 hover:border-[#6E1F2C]/30 p-4 group transition-colors"
            >
              <div className={`absolute top-0 left-0 right-0 h-[2px] ${a.bar}`} />
              <div className="absolute top-2 right-2 inline-flex items-center gap-1 text-[#A6822F]">
                <Lock size={9} />
                <span className="text-[8px] tracking-[0.22em] uppercase">{t("fin.adminBadge")}</span>
              </div>
              <div className={`w-9 h-9 ${a.icon} flex items-center justify-center mb-2`}>
                <Icon size={15} strokeWidth={1.8} />
              </div>
              <p className="text-[9px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-1">{c.label}</p>
              <p className={`font-serif-display text-xl ${a.num} leading-tight`}>{c.value}</p>
              {c.hint && <p className="text-[10px] text-[#7A6B5F] mt-1 italic">{c.hint}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AutomationKpiStrip() {
  const { t } = usePortalLang();
  const [s, setS] = useState(null);
  useEffect(() => {
    portalApi.adminAutomationSummary().then(setS).catch(() => setS({}));
  }, []);
  if (!s) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-7">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white border border-[#6E1F2C]/10 h-20 animate-pulse" />
        ))}
      </div>
    );
  }
  const items = [
    { k: "daily", icon: Clock, label: t("auto.kpi.dailyScheduled"), value: s.daily_scheduled_today ?? 0, accent: "maroon" },
    { k: "weekly", icon: FileText, label: t("auto.kpi.weeklyScheduled"), value: s.weekly_scheduled ?? 0, accent: "maroon" },
    { k: "approval", icon: ShieldCheck, label: t("auto.kpi.awaitingApproval"), value: s.awaiting_approval ?? 0, accent: (s.awaiting_approval ?? 0) > 0 ? "gold" : "neutral" },
    { k: "failed", icon: AlertCircle, label: t("auto.kpi.failedToday"), value: s.failed_today ?? 0, accent: (s.failed_today ?? 0) > 0 ? "red" : "neutral" },
    { k: "sent", icon: Mail, label: t("auto.kpi.sentThisWeek"), value: s.sent_this_week ?? 0, accent: "green" },
  ];
  const accents = {
    maroon: { bar: "bg-[#6E1F2C]", icon: "bg-[#6E1F2C] text-white" },
    gold: { bar: "bg-[#D4A963]", icon: "bg-[#D4A963] text-[#1C110C]" },
    green: { bar: "bg-[#3F6E2C]", icon: "bg-[#3F6E2C] text-white" },
    red: { bar: "bg-[#A3141A]", icon: "bg-[#A3141A] text-white" },
    neutral: { bar: "bg-[#7A6B5F]", icon: "bg-[#7A6B5F] text-white" },
  };
  return (
    <div className="mb-7" data-testid="portal-automation-kpi-strip">
      <div className="flex items-center gap-2 mb-3">
        <Lock size={10} className="text-[#A6822F]" />
        <p className="text-[10px] tracking-[0.32em] uppercase text-[#A6822F]">{t("dash.section.reportAutomation")}</p>
        <div className="flex-1 h-px bg-gradient-to-r from-[#D4A963]/40 to-transparent" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {items.map((c) => {
          const Icon = c.icon;
          const a = accents[c.accent] || accents.maroon;
          return (
            <div
              key={c.k}
              data-testid={`portal-automation-kpi-${c.k}`}
              className="relative bg-white border border-[#6E1F2C]/12 hover:border-[#6E1F2C]/30 p-3 transition-colors"
            >
              <div className={`absolute top-0 left-0 right-0 h-[2px] ${a.bar}`} />
              <div className={`w-8 h-8 ${a.icon} flex items-center justify-center mb-2`}>
                <Icon size={13} strokeWidth={1.8} />
              </div>
              <p className="text-[9px] tracking-[0.26em] uppercase text-[#7A6B5F]">{c.label}</p>
              <p className="font-serif-display text-xl text-[#1C110C] leading-tight mt-0.5">{c.value}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function ProjectCard({ p }) {
  const { t } = usePortalLang();
  const days = (() => {
    if (!p.target_date) return null;
    try {
      const target = new Date(p.target_date.slice(0, 10));
      const diff = Math.round((target - new Date()) / 86400000);
      return diff;
    } catch { return null; }
  })();
  const pct = Math.max(0, Math.min(100, p.progress_percent || 0));
  const isOverdue = days !== null && days < 0;
  return (
    <Link
      to={`/projects/${p.slug}`}
      data-testid={`portal-project-card-${p.slug}`}
      className="group relative block bg-white border border-[#6E1F2C]/15 hover:border-[#6E1F2C]/55 hover:shadow-[0_12px_40px_-18px_rgba(110,31,44,0.4)] transition-all duration-300 overflow-hidden"
    >
      {/* Status accent line */}
      <div className={`absolute top-0 left-0 right-0 h-[3px] ${STATUS_COLOR[p.status] || "bg-[#7A6B5F]"}`} />

      <div className="p-5 md:p-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <span
            className={`text-[9px] tracking-[0.28em] uppercase px-2.5 py-1 ${STATUS_COLOR[p.status] || "bg-[#7A6B5F] text-[#FAF5EC]"}`}
          >
            {t(`status.${p.status === "awaiting_client" ? "awaitingApproval" : p.status}`, STATUS_LABEL[p.status] || p.status)}
          </span>
          {p.code && (
            <span className="text-[10px] text-[#7A6B5F] tracking-[0.18em] uppercase font-mono">
              {p.code}
            </span>
          )}
        </div>

        <h3 className="font-serif-display text-xl text-[#1C110C] group-hover:text-[#6E1F2C] transition-colors leading-snug line-clamp-2 min-h-[3.25rem]">
          {p.name}
        </h3>

        {(p.client_name || p.client_company) && (
          <p className="text-xs text-[#7A6B5F] mt-2 truncate tracking-wide">
            {p.client_company ? `${p.client_company}` : ""}
            {p.client_name && p.client_company ? " · " : ""}
            {p.client_name || ""}
          </p>
        )}
        {p.location && (
          <p className="text-[11px] text-[#3E2A22] mt-1 truncate">{p.location}</p>
        )}

        {/* Progress block */}
        <div className="mt-5 pt-4 border-t border-[#6E1F2C]/10">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] tracking-[0.28em] uppercase text-[#3E2A22] font-medium">
              {t("proj.card.progress")}
            </span>
            <span className="font-serif-display text-base text-[#6E1F2C] leading-none">
              {pct}%
            </span>
          </div>
          <div className="relative h-2 bg-[#FAF5EC] overflow-hidden rounded-full">
            <div
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#D4A963] via-[#A6822F] to-[#6E1F2C] rounded-full transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>

          {p.target_date && (
            <div className="mt-3 flex items-center justify-between gap-2">
              <span className="text-[10px] tracking-[0.18em] uppercase text-[#7A6B5F]">
                {t("proj.card.target", null, { date: p.target_date.slice(0, 10) })}
              </span>
              {days !== null && (
                <span
                  className={`text-[10px] tracking-[0.18em] uppercase font-medium ${
                    isOverdue ? "text-[#A3141A]" : days <= 30 ? "text-[#6E1F2C]" : "text-[#3E2A22]"
                  }`}
                >
                  {isOverdue ? t("proj.card.daysOverdue", null, { n: Math.abs(days) }) : t("proj.card.daysLeft", null, { n: days })}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

function NewProjectModal({ onClose, onCreated }) {
  const empty = {
    name: "", code: "", client_name: "", client_company: "", client_email: "",
    consultant_name: "", consultant_email: "",
    location: "", project_type: "",
    start_date: "", target_date: "",
    project_manager_id: "", site_engineer_id: "",
    description: "", notes: "",
    status: "planning", slug: "",
    internal_team_ids: [], subcontractor_ids: [],
  };
  const [form, setForm] = useState(empty);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [ganttFile, setGanttFile] = useState(null);

  useEffect(() => {
    portalApi.listUsers().then((d) => setUsers(d.users || [])).catch(() => {});
  }, []);

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const autoSlug = (name) =>
    (name || "").toLowerCase().trim()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");

  const handleName = (v) => {
    setField("name", v);
    if (!slugTouched) setField("slug", autoSlug(v));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = { ...form };
      // Strip empties so backend stores nulls / defaults.
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
        else if (Array.isArray(payload[k]) && payload[k].length === 0) delete payload[k];
      });
      const data = await portalApi.createProject(payload);
      onCreated(data, ganttFile);
      onClose();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const cls = "w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-4 py-2.5 text-[#1C110C] text-sm focus:outline-none focus:border-[#6E1F2C]";
  const lbl = "block text-[10px] tracking-[0.3em] uppercase text-[#7A6B5F] mb-2";

  const usersByRole = (role) => users.filter((u) => u.role === role);
  const multiSelect = (field, role) => (
    <select
      multiple
      data-testid={`portal-new-project-${field}`}
      value={form[field]}
      onChange={(e) => setField(field, Array.from(e.target.selectedOptions, (o) => o.value))}
      className={`${cls} h-24`}
    >
      {usersByRole(role).map((u) => (
        <option key={u.id} value={u.id}>{u.name} — {u.email}</option>
      ))}
    </select>
  );

  return (
    <div className="fixed inset-0 z-50 bg-[#1C110C]/60 backdrop-blur-sm flex items-center justify-center px-4 py-6 overflow-y-auto">
      <form
        onSubmit={submit}
        data-testid="portal-new-project-form"
        className="bg-white w-full max-w-[860px] border border-[#6E1F2C]/20 p-7 md:p-10 max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between mb-7">
          <div>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-2">New Project</p>
            <h2 className="font-serif-display text-2xl text-[#1C110C]">Create a detailed project</h2>
            <p className="text-xs text-[#7A6B5F] mt-1">All fields except project name are optional and can be updated later.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-[#7A6B5F] hover:text-[#1C110C]"><X size={18} /></button>
        </div>

        {/* PROJECT BASICS */}
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#6E1F2C] mb-3">Project basics</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={lbl}>Project name *</label>
            <input data-testid="portal-new-project-name" required value={form.name} onChange={(e) => handleName(e.target.value)} className={cls} />
          </div>
          <div>
            <label className={lbl}>Project code / reference</label>
            <input data-testid="portal-new-project-code" value={form.code} onChange={(e) => setField("code", e.target.value)} className={cls} placeholder="e.g. 4601" />
          </div>
          <div className="sm:col-span-2">
            <label className={lbl}>URL slug <span className="text-[#7A6B5F] normal-case tracking-normal">(editable)</span></label>
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#7A6B5F] whitespace-nowrap">/projects/</span>
              <input
                data-testid="portal-new-project-slug"
                value={form.slug}
                onChange={(e) => { setSlugTouched(true); setField("slug", autoSlug(e.target.value)); }}
                placeholder="auto-generated from name"
                className={cls}
              />
            </div>
          </div>
          <div>
            <label className={lbl}>Project type</label>
            <input data-testid="portal-new-project-type" value={form.project_type} onChange={(e) => setField("project_type", e.target.value)} className={cls} placeholder="Villa / Penthouse / Apartment / Office" />
          </div>
          <div>
            <label className={lbl}>Location</label>
            <input data-testid="portal-new-project-location" value={form.location} onChange={(e) => setField("location", e.target.value)} className={cls} placeholder="Palm Tower #4601, Dubai" />
          </div>
          <div>
            <label className={lbl}>Project status</label>
            <select data-testid="portal-new-project-status" value={form.status} onChange={(e) => setField("status", e.target.value)} className={cls}>
              <option value="planning">Planning</option>
              <option value="active">Active</option>
              <option value="awaiting_client">Awaiting client</option>
              <option value="delayed">Delayed</option>
              <option value="handover">Handover</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <div>
            <label className={lbl}>Start date</label>
            <input type="date" data-testid="portal-new-project-start" value={form.start_date} onChange={(e) => setField("start_date", e.target.value)} className={cls} />
          </div>
          <div>
            <label className={lbl}>Target completion</label>
            <input type="date" data-testid="portal-new-project-target" value={form.target_date} onChange={(e) => setField("target_date", e.target.value)} className={cls} />
          </div>
        </div>

        {/* CLIENT & CONSULTANT */}
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#6E1F2C] mt-7 mb-3">Client & consultant</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className={lbl}>Client name</label><input data-testid="portal-new-project-client-name" value={form.client_name} onChange={(e) => setField("client_name", e.target.value)} className={cls} /></div>
          <div><label className={lbl}>Client company</label><input data-testid="portal-new-project-client-company" value={form.client_company} onChange={(e) => setField("client_company", e.target.value)} className={cls} /></div>
          <div><label className={lbl}>Client contact email</label><input type="email" data-testid="portal-new-project-client-email" value={form.client_email} onChange={(e) => setField("client_email", e.target.value)} className={cls} /></div>
          <div><label className={lbl}>Consultant / designer name</label><input data-testid="portal-new-project-consultant-name" value={form.consultant_name} onChange={(e) => setField("consultant_name", e.target.value)} className={cls} /></div>
          <div className="sm:col-span-2"><label className={lbl}>Consultant / designer email</label><input type="email" data-testid="portal-new-project-consultant-email" value={form.consultant_email} onChange={(e) => setField("consultant_email", e.target.value)} className={cls} /></div>
        </div>

        {/* TEAM */}
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#6E1F2C] mt-7 mb-3">Internal team & subcontractors</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={lbl}>Project manager</label>
            <select data-testid="portal-new-project-pm" value={form.project_manager_id} onChange={(e) => setField("project_manager_id", e.target.value)} className={cls}>
              <option value="">— None —</option>
              {usersByRole("project_manager").map((u) => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>Site engineer / supervisor</label>
            <select data-testid="portal-new-project-se" value={form.site_engineer_id} onChange={(e) => setField("site_engineer_id", e.target.value)} className={cls}>
              <option value="">— None —</option>
              {usersByRole("site_engineer").map((u) => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>Time Furniture team (Cmd/Ctrl-click for multi)</label>
            {multiSelect("internal_team_ids", "site_engineer")}
            <p className="text-[10px] text-[#7A6B5F] mt-1">Additional site engineers, foremen, supervisors.</p>
          </div>
          <div>
            <label className={lbl}>Subcontractors / suppliers (multi)</label>
            {multiSelect("subcontractor_ids", "subcontractor")}
            <p className="text-[10px] text-[#7A6B5F] mt-1">Marble, joinery, MEP, etc. Add them in Users & Access first.</p>
          </div>
        </div>

        {/* SCOPE & NOTES */}
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#6E1F2C] mt-7 mb-3">Scope & notes</p>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className={lbl}>Description / scope summary</label>
            <textarea data-testid="portal-new-project-description" rows={3} value={form.description} onChange={(e) => setField("description", e.target.value)} className={cls} placeholder="Full villa fit-out across 3 levels with marble flooring, custom joinery, German kitchen…" />
          </div>
          <div>
            <label className={lbl}>Internal notes (not visible to clients)</label>
            <textarea data-testid="portal-new-project-notes" rows={2} value={form.notes} onChange={(e) => setField("notes", e.target.value)} className={cls} />
          </div>
        </div>

        {/* PROGRAMME / GANTT */}
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#6E1F2C] mt-7 mb-3">Project programme / Gantt</p>
        <p className="text-xs text-[#3E2A22] mb-3">
          Optional — upload your project execution Excel now and we&apos;ll parse phases, tasks and package coordination after the project is created. You can also upload it later from the Timeline / Gantt tab.
        </p>
        <label
          htmlFor="portal-new-project-gantt-file"
          className="border-2 border-dashed border-[#6E1F2C]/30 hover:border-[#6E1F2C] block p-6 text-center cursor-pointer transition-colors"
        >
          <FileSpreadsheet className="mx-auto text-[#6E1F2C] mb-2" size={28} />
          {ganttFile ? (
            <>
              <p className="text-sm text-[#1C110C]">{ganttFile.name}</p>
              <p className="text-[10px] text-[#7A6B5F] mt-1">{(ganttFile.size / 1024).toFixed(0)} KB · ready to parse after create</p>
            </>
          ) : (
            <>
              <p className="text-sm text-[#1C110C]">Click to select Gantt / Programme Excel</p>
              <p className="text-[10px] text-[#7A6B5F] mt-1">.xlsx · .xlsm · .xls · max 100 MB</p>
            </>
          )}
          <input
            id="portal-new-project-gantt-file"
            data-testid="portal-new-project-gantt-file"
            type="file"
            accept=".xlsx,.xlsm,.xls"
            onChange={(e) => setGanttFile(e.target.files?.[0] || null)}
            className="hidden"
          />
        </label>
        {ganttFile && (
          <button
            type="button"
            onClick={() => setGanttFile(null)}
            className="text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] hover:text-[#6E1F2C] mt-2"
          >
            Remove file
          </button>
        )}

        {err && <p data-testid="portal-new-project-error" className="text-xs text-[#A3141A] mt-5">{err}</p>}
        <button
          type="submit"
          disabled={busy}
          data-testid="portal-new-project-submit"
          className="w-full mt-7 bg-[#6E1F2C] text-[#FAF5EC] px-6 py-3.5 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
        >
          {ganttFile && !busy && <Upload size={14} />}
          {busy ? "Creating…" : ganttFile ? "Create project & parse programme" : "Create project"}
        </button>
      </form>
    </div>
  );
}

function NewUserModal_DEPRECATED() { return null; }

/**
 * One dashboard component that renders the same project list with:
 *   - title + breadcrumbs that match the requested role landing
 *   - Admin / PM-only "+ New Project" and "+ New User" buttons
 *   - Auto-redirect for single-project Client/Consultant/Sub roles
 */
export default function PortalDashboard({ variant = "admin" }) {
  const { user } = usePortalAuth();
  const { t } = usePortalLang();
  const [projects, setProjects] = useState(null);
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const [refreshFlag, setRefreshFlag] = useState(0);
  // After "Create project & parse programme" we open the wizard with the file pre-loaded.
  const [postCreateWizard, setPostCreateWizard] = useState(null); // {slug, file}
  const navigate = useNavigate();

  useEffect(() => {
    portalApi.listProjects().then((d) => setProjects(d.projects || [])).catch(() => setProjects([]));
  }, [refreshFlag]);

  // Auto-redirect single-project external users straight to their project.
  // Guard: only fire once per (user, slug) pair so a re-render storm cannot
  // hammer history.replaceState (the live "more than 100 times per 10 seconds"
  // bug). useRef sentinel survives across renders without triggering effects.
  const autoRedirectedRef = React.useRef(null);
  useEffect(() => {
    if (!user || !projects) return;
    const external = ["client", "consultant", "subcontractor"].includes(user.role);
    if (!external || projects.length !== 1) return;
    const target = `/projects/${projects[0].slug}`;
    if (autoRedirectedRef.current === target) return; // already redirected once
    if (window.location.pathname === target) return;  // already on the page
    autoRedirectedRef.current = target;
    navigate(target, { replace: true });
  }, [user, projects, navigate]);

  // NOTE: do NOT return early when !user — PortalLayout (rendered below)
  // is the auth gate that issues the Navigate-to-/projects redirect. If we
  // short-circuit here, the user sees a blank page on /projects/admin etc.
  // PortalLayout shows its own loading state, so flicker is not an issue.
  const isInternal = user && ["admin", "project_manager", "site_engineer"].includes(user.role);

  // If a non-admin tries to access /projects/admin, bounce them.
  // react-router's <Navigate> already guards against re-firing per mount;
  // the production replaceState() loop was caused by PortalLogin's
  // fallback sending unknown-role users HERE (to /projects/admin), where
  // we'd bounce them back. PortalLogin now uses SAFE_LANDING for unknown
  // roles, so this path only triggers for users we definitively know are
  // not admins — and it fires once per mount.
  if (
    user &&
    variant === "admin" &&
    user.role !== "admin" &&
    window.location.pathname !== "/projects"
  ) {
    return <Navigate to="/projects" replace />;
  }

  const titleByVariant = {
    admin: t("dash.title.admin"),
    manager: t("dash.title.manager"),
    site: t("dash.title.site"),
    my: t("dash.title.my"),
  };
  const title = titleByVariant[variant] || t("nav.projects");

  return (
    <PortalLayout title={title} breadcrumbs={[{ label: title }]}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-7">
        <p className="text-sm text-[#3E2A22]">
          {projects === null
            ? t("msg.loadingProjects")
            : projects.length === 0
            ? t("msg.noProjects")
            : t(projects.length === 1 ? "proj.count.one" : "proj.count.other", null, { n: projects.length })}
        </p>
        {isInternal && (
          <div className="flex flex-wrap items-center gap-3">
            {user?.role === "admin" && (
              <Link
                to="/projects/users"
                data-testid="portal-users-link"
                className="inline-flex items-center gap-2 border border-[#6E1F2C] text-[#6E1F2C] px-4 py-2.5 text-[10px] tracking-[0.28em] uppercase hover:bg-[#6E1F2C] hover:text-[#FAF5EC] transition-colors"
              >
                <Users size={14} /> {t("btn.usersAccess")}
              </Link>
            )}
            {(user?.role === "admin" || user?.role === "project_manager") && (
              <button
                onClick={() => setNewProjectOpen(true)}
                data-testid="portal-new-project-btn"
                className="inline-flex items-center gap-2 bg-[#6E1F2C] text-[#FAF5EC] px-4 py-2.5 text-[10px] tracking-[0.28em] uppercase hover:bg-[#56151F] transition-colors"
              >
                <Plus size={14} /> {t("btn.newProject")}
              </button>
            )}
          </div>
        )}
      </div>

      {isInternal && (user?.role === "admin" || user?.role === "project_manager") && <SummaryStrip />}

      {user?.role === "admin" && <FinancialKpiStrip />}
      {user?.role === "admin" && <AutomationKpiStrip />}

      {projects === null ? null : projects.length === 0 ? (
        <div className="bg-white border border-dashed border-[#6E1F2C]/25 p-12 text-center">
          <FolderOpen size={28} className="mx-auto text-[#D4A963] mb-4" />
          <p className="text-sm text-[#3E2A22] max-w-md mx-auto">
            {isInternal ? t("msg.noProjectsAdmin") : t("msg.noProjectsClient")}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {projects.map((p) => (<ProjectCard key={p.id} p={p} />))}
        </div>
      )}

      {newProjectOpen && (
        <NewProjectModal
          onClose={() => setNewProjectOpen(false)}
          onCreated={(data, file) => {
            setRefreshFlag((n) => n + 1);
            if (file && data?.slug) {
              setPostCreateWizard({ slug: data.slug, file });
            }
          }}
        />
      )}
      {postCreateWizard && (
        <GanttUploadWizard
          slug={postCreateWizard.slug}
          open={true}
          initialFile={postCreateWizard.file}
          onClose={() => setPostCreateWizard(null)}
          onImported={() => {
            // Take admin straight to the new project's timeline tab
            const slug = postCreateWizard.slug;
            setPostCreateWizard(null);
            navigate(`/projects/${slug}`);
          }}
        />
      )}
    </PortalLayout>
  );
}
